import {
  checkNullString,
  getLastDateRange,
  getStaticDateText,
} from '@/pages/datalab/utils/utils';
import type { IColumnInfo, IColumnRawInfo } from '@dbl/core/dataSource';
import { ChartType, type TChartType } from '@dbl/core/chart';
import {
  FieldTypeDefaultAccuracy,
  getDefaultNumberUnit,
} from '@dbl/core/chartEditor';
import { normalizeFilterField } from '@/pages/datalab/components/SortableDrag/utils/filterSortablePersist';
import { normalizeMetricField } from '@/pages/datalab/components/SortableDrag/utils/metricSortablePersist';
import type { IApplyBatchFieldsParams } from '../types';
import {
  evaluateBatchFieldDisable,
  getChartLimit,
  isOverChartLimit,
} from './batchFieldDisableRules';
import { getFieldDragIdentity, keysEqual } from './fieldIdentity';
import { buildFieldTree, flattenTreeFields } from './buildFieldTree';

/** 维度区 transform，补齐 accuracy / unit 默认值 */
const transformDimensionField = (
  item: IColumnRawInfo,
  chartType: string,
): IColumnInfo => {
  const field = item as IColumnInfo;
  const dimensionField: IColumnInfo = {
    ...item,
    accuracy: field.accuracy ?? FieldTypeDefaultAccuracy[item.column_type],
    unit: (field.unit ??
      getDefaultNumberUnit({ chartType, fieldType: item.column_type })) as IColumnInfo['unit'],
  };
  return dimensionField;
};

/** rawField 按 getFieldDragIdentity 建索引，便于勾选 key 反查字段元数据 */
const buildFieldKeyMap = (
  fields: IColumnRawInfo[],
): Map<string, IColumnRawInfo> => {
  const map = new Map<string, IColumnRawInfo>();
  fields.forEach(field => {
    map.set(getFieldDragIdentity(field), field);
  });
  return map;
};

/** 下拉打开时预构建字段索引，关闭写回时复用避免重复遍历 dataTables */
export const buildBatchFieldKeyMap = (
  dataTables: IApplyBatchFieldsParams['ctx']['dataTables'],
  customColumns: IApplyBatchFieldsParams['ctx']['customColumns'],
  isEn: boolean,
  customFieldLabel: string,
): Map<string, IColumnRawInfo> =>
  buildFieldKeyMap(
    flattenTreeFields(
      buildFieldTree(dataTables, customColumns, isEn, customFieldLabel),
    ),
  );

/** 关闭浮窗时按勾选集同步操作区；各 targetKey 路由到对应 persist，与单条 close/拖拽副作用对齐 */
export const applyBatchFields = (params: IApplyBatchFieldsParams): void => {
  const {
    selectedKeys,
    openSnapshotKeys,
    targetKey,
    existingColumns,
    ctx,
    formatMessage,
  } = params;

  // 勾选集与打开时快照一致则跳过 dispatch，避免无意义写 model
  if (keysEqual(selectedKeys, openSnapshotKeys)) {
    return;
  }

  // 排除占位项（id 为空字符串），只处理真实 IColumnInfo
  const validExisting = (existingColumns || []).filter(
    item => !checkNullString(item.id),
  );
  // retained：最终仍勾选的操作区既有项，保留筛选值/聚合/unit 等用户已配属性
  const retained = validExisting.filter(item =>
    selectedKeys.has(getFieldDragIdentity(item)),
  );

  const disableContext = {
    targetKey,
    chartType: ctx.chartType,
    existingColumns: validExisting,
    selectedKeys,
    openSnapshotKeys,
  };

  const limitRule = getChartLimit(targetKey, ctx.chartType);
  const newItems: IColumnInfo[] = [];

  // 存在相对 openSnapshotKeys 的新增 key 时才构建字段树，纯取消勾选无需遍历 dataTables
  const hasNewSelections = Array.from(selectedKeys).some(
    key => !openSnapshotKeys.has(key),
  );
  if (hasNewSelections) {
    const fieldMap =
      ctx.fieldKeyMap ??
      buildFieldKeyMap(
        flattenTreeFields(
          buildFieldTree(
            ctx.dataTables,
            ctx.customColumns,
            ctx.isEn,
            formatMessage('batch_add_custom_field'),
          ),
        ),
      );

    // 仅处理相对打开快照的新增勾选；UI 已 disable 的字段在此作写回兜底
    selectedKeys.forEach(key => {
      // 打开快照中已有的 key 已在 retained 中，无需再 normalize
      if (openSnapshotKeys.has(key)) {
        return;
      }
      const rawField = fieldMap.get(key);
      if (!rawField) {
        return;
      }
      if (evaluateBatchFieldDisable(rawField, disableContext)) {
        return;
      }
      // 写回侧上限口径：retained 数量 + 已接纳 newItems + 当前候选 1 条
      if (isOverChartLimit(limitRule, retained.length + newItems.length + 1)) {
        return;
      }

      if (targetKey === 'columnFilters') {
        newItems.push(normalizeFilterField(rawField as IColumnInfo));
      } else if (targetKey === 'columnMetrics') {
        newItems.push(normalizeMetricField(rawField as IColumnInfo, ctx.chartType));
      } else if (
        targetKey === 'columnDimensions' ||
        targetKey === 'columnRawColumns' ||
        targetKey === 'columnX' ||
        targetKey === 'columnShowDimensions'
      ) {
        newItems.push(transformDimensionField(rawField, ctx.chartType));
      }
    });
  }

  const merged = [...retained, ...newItems];
  const removed = validExisting.filter(
    item => !selectedKeys.has(getFieldDragIdentity(item)),
  );

  // 以下按 targetKey 分发到各 Sortable persist，保证批量路径与单条增删副作用一致
  // 筛选区：批量取消勾选走 filterPersist，与 close 删除 compareTimes / Trend 维度联动一致
  if (targetKey === 'columnFilters' && ctx.filterPersist) {
    ctx.filterPersist.applyBatchChange(merged, removed);
    return;
  }

  // 指标区：批量增删走 metricPersist，关闭时 save + 字段列排序 metricUpdate
  if (targetKey === 'columnMetrics' && ctx.metricPersist) {
    ctx.metricPersist.applyBatchChange(merged);
    return;
  }

  // 分类维度：批量增删走 dimensionPersist，同步表格字段列排序
  if (targetKey === 'columnDimensions' && ctx.dimensionPersist) {
    /**
     * trendFirstDimInvalid：Trend 类图表合并后首维非 date
     * - merged.length > 0：合并后仍有维度
     * - merged[0].column_type !== 'date'：首维不是 date
     * 须同时 newItems.length > 0 才拦截：允许用户仅取消勾选 date 首维（纯删除不拦）
     */
    const trendFirstDimInvalid =
      [ChartType.Trend, ChartType.DimNumberTrend].includes(ctx.chartType as TChartType) &&
      merged.length > 0 &&
      merged[0].column_type !== 'date';
    if (trendFirstDimInvalid && newItems.length > 0) {
      return;
    }

    ctx.dimensionPersist.applyBatchChange(merged, removed);
    return;
  }

  // 表格原始列：批量增删走 rawColumnPersist，删除时清理 chart 配置
  if (targetKey === 'columnRawColumns' && ctx.rawColumnPersist) {
    ctx.rawColumnPersist.applyBatchChange(merged, removed);
    return;
  }

  // 展示维度：批量增删走 showDimensionPersist，与拖拽落库校验对齐
  if (targetKey === 'columnShowDimensions' && ctx.showDimensionPersist) {
    /**
     * showDimFirstInvalid：DimNumberTrend 展示维度首项为 date/time 时非法
     * newItems.length > 0 同理：纯删除 date 首项允许，仅在有新增时拦截写回
     */
    const showDimFirstInvalid =
      ctx.chartType === ChartType.DimNumberTrend &&
      merged.length > 0 &&
      ['date', 'time'].includes(merged[0].column_type || '');
    if (showDimFirstInvalid && newItems.length > 0) {
      return;
    }

    const applied = ctx.showDimensionPersist.applyBatchChange(merged, removed);
    // applied 为 false 表示 persist 内部校验未通过（如映射冲突），不触发后续 UI 回调
    if (applied) {
      ctx.onAfterBatchApply?.(merged);
    }
    return;
  }

  const savePayload: Record<string, IColumnInfo[]> = {
    [targetKey]: merged,
  };

  // 无专用 persist 的落区（如 columnX）走通用 save；Trend 类联动补日期筛选器
  if (
    (targetKey === 'columnDimensions' || targetKey === 'columnX') &&
    [ChartType.Trend, ChartType.DimNumberTrend].includes(ctx.chartType as TChartType) &&
    merged.length > 0
  ) {
    const dateInfo = merged[0];
    const filters = ctx.filters || [];
    // 首维为 date 且筛选区尚未包含同 id 字段时，自动追加「近 1 天」日期筛选（对齐 dimensionSortable.setList）
    if (dateInfo.column_type === 'date' && !filters.some(item => item.id === dateInfo.id)) {
      const dateTypeVal = dateInfo.column_granularity?.replace(/\d/, '') || 'day';
      const text = getStaticDateText(getLastDateRange(1, dateTypeVal), dateTypeVal);
      const addDateFilter: IColumnInfo = {
        ...dateInfo,
        column_text: text,
        column_value: [`1-${dateTypeVal}`],
        dateType: dateTypeVal,
        lastCount: 1,
        operation: 'recent',
      };
      savePayload.columnFilters = [
        ...filters.filter(item => !checkNullString(item.id)),
        addDateFilter,
      ];
    }
  }

  ctx.dispatchFun('save', savePayload);
};

/** 打开下拉时记录操作区字段 key 快照；关闭时与 selectedKeys diff 判断是否有变更 */
export const buildOpenSnapshotKeys = (
  existingColumns: IColumnInfo[],
): Set<string> => {
  const keys = new Set<string>();
  (existingColumns || [])
    .filter(item => !checkNullString(item.id))
    .forEach(item => {
      keys.add(getFieldDragIdentity(item));
    });
  return keys;
};

